import { isUnavailableToken as coreIsUnavailableToken } from '@/utils/responseGuard'

// UNAVAILABLE_TOKENS 已删除 —— token 分类统一走 responseGuard 核(union)
const ENVELOPE_KEYS = [
  'content',
  'payload',
  'body',
  'extra',
  'page',
  'data',
  'list',
  'records',
  'dataList',
  'diagnostics',
  'diagnostic',
  'meta',
  'detail',
  'details',
  'error',
  'errors'
]
const MAX_ENVELOPE_DEPTH = 6
const PAYLOAD_HINT_KEYS = [
  'orderRecordList',
  'capitalDetailsList',
  'bankCardList',
  'bankList',
  'paymentList',
  'list',
  'records',
  'dataList',
  'pageTotalCount',
  'totalCount',
  'orderNo',
  'recordNo',
  'wdOrderNo',
  'orderStatus',
  'orderStatusName',
  'orderAmount',
  'bankCard',
  'bankName',
  'realName',
  'phone',
  'phoneNo',
  'privacyPasswdSetted'
]
const METADATA_KEYS = [
  'status',
  'state',
  'result',
  'code',
  'errorCode',
  'success',
  'msg',
  'message',
  'errorMsg',
  'errorMessage',
  'reason',
  'desc',
  'description'
]
// Marker keys that can show up as top-level "notes" (mock/fallback/readOnly) even when
// the real business payload is nested in payload/body/content.
const RESPONSE_MARKER_KEYS = [
  'readOnly',
  'readOnlyQuery',
  'readOnlyAction',
  'readOnlyMode',
  'readonly',
  'read_only',
  'read_only_query',
  'read_only_action',
  'read_only_mode',
  'mock',
  'mockMode',
  'mock_mode',
  'mockFallback',
  'mock_fallback',
  'fallback',
  'fallbackUsed',
  'fallback_used',
  'failClosed',
  'fail_closed',
  'stub',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'notSupported',
  'not_supported',
  'unsupported',
  'dataUnavailable',
  'data_unavailable',
  'routeUnavailable',
  'route_unavailable',
  'notConnected',
  'not_connected',
  'queryOnly',
  'query_only',
  'onlyQuery',
  'only_query',
  'writeBlocked',
  'write_blocked',
  'backendBlocked',
  'backend_blocked',
  'paymentBackendBlocked',
  'payment_backend_blocked',
  'paymentStatusUnconfirmed',
  'payment_status_unconfirmed',
  'falseProof',
  'false_proof',
  'blocked',
  'need_login',
  'readFailure',
  'read_failure',
  'readUnavailable',
  'read_unavailable',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'backendFailure',
  'backend_failure',
  'readBlocked',
  'read_blocked',
  'readFailed',
  'read_failed'
]
const RESPONSE_METADATA_KEYS = [...METADATA_KEYS, ...RESPONSE_MARKER_KEYS]
const WRITE_PROOF_KEYS = []
const SERVER_READ_PROOF_KEYS = ['serverReadProof', 'server_read_proof', 'readProof', 'read_proof']
const SERVER_WRITE_PROOF_KEYS = [
  'serverProof',
  'server_proof',
  'writeProof',
  'write_proof',
  'affectedProof',
  'affected_proof',
  'affectedRowsProof',
  'affected_rows_proof'
]
const WRITE_COUNT_PROOF_KEYS = ['affected', 'affectedRows', 'rowCount']
const PAYMENT_STATUS_PROOF_KEYS = [
  'serverOrderStatusProof',
  'server_order_status_proof',
  'statusProof',
  'status_proof',
  'orderProof',
  'order_proof',
  'paymentUrlProof',
  'payment_url_proof',
  'payProof',
  'pay_proof',
  'withdrawProof',
  'withdraw_proof',
  'withdrawOrderProof',
  'withdraw_order_proof',
  'withdrawStatusProof',
  'withdraw_status_proof'
]
const PAYMENT_FALSE_PROOF_KEYS = [
  ...PAYMENT_STATUS_PROOF_KEYS,
  ...SERVER_WRITE_PROOF_KEYS,
  'serverRewardProof',
  'server_reward_proof',
  'rewardProof',
  'reward_proof',
  'rewardIssueProof',
  'reward_issue_proof'
]
const ORDER_WRITABLE_KEYS = [
  'orderWritable',
  'order_writable'
]
const FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder']
const TECHNICAL_MARKER_KEYS = ['technicalMarker', 'technical_marker']
const BACKEND_FAILURE_KEYS = ['backendFailure', 'backend_failure']

export function isNeedLogin(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const flags = [
    res && res.needLogin,
    res && res.need_login,
    content.needLogin,
    content.need_login,
    ...bodies.flatMap((body) => [
      body.needLogin,
      body.need_login
    ])
  ]
  if (flags.some(isNeedLoginFlag)) return true
  return !!(res && [
    res.status,
    res.state,
    res.code,
    res.errorCode,
    res.msg,
    res.message,
    res.errorMsg,
    res.errorMessage,
    res.reason,
    res.desc,
    res.description,
    content.status,
    content.state,
    content.code,
    content.errorCode,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    ...bodies.flatMap((body) => [
      body.status,
      body.state,
      body.code,
      body.errorCode,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description
    ])
  ].some(isNeedLoginToken))
}

function isNeedLoginToken(value) {
  const token = unavailableToken(value)
  return token === 'need_login' ||
    token.includes('need_login') ||
    token === 'needlogin' ||
    token.includes('needlogin') ||
    token === 'login_required' ||
    token.includes('login_required') ||
    token.includes('login required') ||
    token === '401' ||
    /(^|[^\d])401([^\d]|$)/.test(token) ||
    token === 'unauthorized' ||
    token.includes('unauthorized') ||
    token === 'token_expired' ||
    token.includes('token_expired') ||
    token === 'expired_token' ||
    token.includes('expired_token') ||
    token === 'login_expired' ||
    token.includes('login_expired') ||
    token === 'session_expired' ||
    token.includes('session_expired') ||
    (token.includes('token') && token.includes('expired')) ||
    (token.includes('session') && token.includes('expired'))
}

function isNeedLoginFlag(value) {
  const token = unavailableToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token) ||
    isNeedLoginToken(value)
}

function unavailableToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasFailureWord(value) {
  const token = unavailableToken(value)
  return !!token && (
    ['fail', 'failed', 'failure', 'error'].includes(token) ||
    /(?:^|[\s_-])(?:fail|failed|failure|error)(?:$|[\s_-])/.test(token) ||
    /\b(?:pay|payment|withdraw|order|request|operation|backend|route|api|server|status)[\s_-]+(?:failed|fail|failure|error)\b/.test(token) ||
    /\b(?:failed|fail|failure|error)[\s_-]+(?:pay|payment|withdraw|order|request|operation|backend|route|api|server|status)\b/.test(token)
  )
}

function isTerminalFailureToken(value) {
  const token = unavailableToken(value)
  return ['2', 'cancel', 'cancelled', 'canceled', 'reject', 'rejected', 'expired', 'timeout'].includes(token) ||
    hasFailureWord(value) ||
    token.includes('reject') ||
    token.includes('cancel') ||
    token.includes('expired') ||
    token.includes('timeout')
}

function isUnavailableToken(value) {
  return coreIsUnavailableToken(value)
}

function isHardUnavailableText(value) {
  const token = unavailableToken(value)
  return isUnavailableToken(value) && (
    token.includes('route_unavailable') ||
    token.includes('route-unavailable') ||
    token.includes('route unavailable') ||
    token.includes('routeunavailable') ||
    token.includes('technical_error') ||
    token.includes('technical-error') ||
    token.includes('technical error') ||
    token.includes('technicalerror') ||
    token.includes('technical_marker') ||
    token.includes('technical-marker') ||
    token.includes('technical marker') ||
    token.includes('technicalmarker') ||
    token.includes('technical_exception') ||
    token.includes('technical-exception') ||
    token.includes('technical exception') ||
    token.includes('technicalexception') ||
    token.includes('backend_failure') ||
    token.includes('backend-failure') ||
    token.includes('backend failure') ||
    token.includes('backendfailure') ||
    token.includes('技术错误') ||
    token.includes('技术异常')
  )
}

function isVisibleTechnicalFailureMessage(value) {
  const token = unavailableToken(value)
  return (token.includes('技术错误') || token.includes('技术异常')) && !hasRawTechnicalDetail(value)
}

function hasRawTechnicalDetail(value) {
  const message = String(value == null ? '' : value).trim()
  const token = unavailableToken(message)
  return token.includes('upstream') ||
    token.includes('route failed') ||
    token.includes('request failed') ||
    token.includes('network error') ||
    token.includes('timeout') ||
    token.includes('status code') ||
    token.includes('axios') ||
    /\beconn[a-z_]*\b/i.test(message) ||
    /\b(typeerror|referenceerror|syntaxerror|rangeerror|urierror)\b/i.test(message) ||
    /\b(stack trace|traceid|trace id)\b/i.test(message) ||
    /\bhttps?:\/\/\S+/i.test(message)
}

function unavailableFallback(fallback, message) {
  return fallback && fallback !== 'fallback' && !isOptimisticSuccessMessage(fallback)
    ? fallback
    : message
}

function isBlockedToken(value) {
  const token = unavailableToken(value)
  if (!token || token.includes('unblocked') || token.includes('not blocked')) return false
  return token === 'block' ||
    token === 'blocked' ||
    token === 'backendblocked' ||
    token === 'paymentbackendblocked' ||
    token.includes('backend_blocked') ||
    token.includes('backendblocked') ||
    token.includes('backend blocked') ||
    token.includes('backend-blocked') ||
    token.includes('payment_backend_blocked') ||
    token.includes('paymentbackendblocked') ||
    token.includes('payment backend blocked') ||
    token.includes('payment-backend-blocked') ||
    /(^|[\s_-])blocked($|[\s_-])/.test(token)
}

function isActiveGuardFlag(value) {
  const token = unavailableToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(token)
}

function isFalseProofValue(value) {
  const token = unavailableToken(value)
  return value === false || value === 0 || ['false', '0', 'no', 'off'].includes(token) || FALSEY_PROOF_TOKENS.includes(token)
}

function markerValuesForKeys(sources, keys) {
  return sources.flatMap((source) => {
    if (!source || typeof source !== 'object') return []
    return keys.map((key) => source[key])
  })
}

function hasActiveMarkerValues(values) {
  return values.some((value) => isActiveGuardFlag(value) || isUnavailableToken(value))
}

function hasTechnicalMarkerAlias(payload, content = contentOf(payload), bodies = responseBodies(payload)) {
  return hasActiveMarkerValues(markerValuesForKeys([payload, content, ...bodies], TECHNICAL_MARKER_KEYS))
}

function hasBackendFailureAlias(payload, content = contentOf(payload), bodies = responseBodies(payload)) {
  return hasActiveMarkerValues(markerValuesForKeys([payload, content, ...bodies], BACKEND_FAILURE_KEYS))
}

function hasTechnicalAliasToken(values) {
  return values.some((value) => {
    const token = unavailableToken(value)
    return token.includes('technical_marker') ||
      token.includes('technical-marker') ||
      token.includes('technical marker') ||
      token.includes('technicalmarker') ||
      token.includes('technical_error') ||
      token.includes('technical-error') ||
      token.includes('technical error') ||
      token.includes('technicalerror') ||
      token.includes('technical_exception') ||
      token.includes('technical-exception') ||
      token.includes('technical exception') ||
      token.includes('technicalexception') ||
      token.includes('技术错误') ||
      token.includes('技术异常')
  })
}

function hasBackendFailureAliasToken(values) {
  return values.some((value) => {
    const token = unavailableToken(value)
    return token.includes('backend_failure') ||
      token.includes('backend-failure') ||
      token.includes('backend failure') ||
      token.includes('backendfailure')
  })
}

function hasServerReadProof(payload, content = contentOf(payload), bodies = responseBodies(payload)) {
  return [payload, content, ...bodies].some((source) => {
    if (!source || typeof source !== 'object') return false
    return SERVER_READ_PROOF_KEYS.some((key) => isActiveGuardFlag(source[key]))
  })
}

function readOnlyQueryGuardValues(payload, content = contentOf(payload), bodies = responseBodies(payload)) {
  if (hasServerReadProof(payload, content, bodies)) return []
  return [
    payload && payload.readOnlyQuery,
    payload && payload.read_only_query,
    content.readOnlyQuery,
    content.read_only_query,
    ...bodies.flatMap((body) => [body.readOnlyQuery, body.read_only_query])
  ]
}

function isPositiveProofValue(value) {
  if (Array.isArray(value)) return false
  if (value && typeof value === 'object') return false
  if (typeof value === 'boolean') return value === true
  const valueText = text(value).trim()
  if (!valueText) return false
  const token = unavailableToken(valueText)
  if (FALSEY_PROOF_TOKENS.includes(token)) return false
  if (isUnavailableToken(token)) return false
  return true
}

function isStrictUnavailableMarker(value) {
  const token = unavailableToken(value)
  if (!token || value === false || value === 0 || ['false', '0', 'no', 'off'].includes(token)) return false
  if (isActiveGuardFlag(value)) return true
  return coreIsUnavailableToken(token) || isBlockedToken(token)
}

function isHardUnavailableToken(value) {
  const token = unavailableToken(value)
  if (!token) return false
  const compactToken = token.replace(/[\s_:-]+/g, '')
  // Hard failure/block markers should always fail closed, even if other nested business
  // payload exists elsewhere in the response.
  if (token.includes('failclosed') || token.includes('fail_closed') || token.includes('fail-closed') || token.includes('fail closed')) return true
  if (token.includes('placeholder')) return true
  if (isBlockedToken(token)) return true
  if (token.includes('backend_blocked') || token.includes('backend blocked') || token.includes('backend-blocked') || token.includes('backendblocked')) return true
  if (token.includes('backend_failure') || token.includes('backend failure') || token.includes('backend-failure') || token.includes('backendfailure')) return true
  if (token.includes('payment_backend_blocked') || token.includes('payment backend blocked') || token.includes('payment-backend-blocked') || token.includes('paymentbackendblocked')) return true
  if (token.includes('false_proof') || token.includes('falseproof') || token.includes('false proof') || token.includes('false-proof')) return true
  if (['writeproof', 'serverproof', 'affectedproof', 'affectedrowsproof', 'statusproof', 'orderproof', 'paymentproof', 'payproof', 'withdrawproof'].some((proofKey) => compactToken.includes(proofKey)) && compactToken.includes('false')) return true
  if (token.includes('read_failure') || token.includes('read failure') || token.includes('read-failure') || token.includes('read_failed') || token.includes('read failed') || token.includes('read-failed')) return true
  if (token.includes('read_unavailable') || token.includes('read unavailable') || token.includes('read-unavailable') || token.includes('readunavailable')) return true
  if (token.includes('read_blocked') || token.includes('read blocked') || token.includes('read-blocked') || token.includes('readblocked')) return true
  if (token.includes('technical_error') || token.includes('technical error') || token.includes('technical-error') || token.includes('technicalerror')) return true
  if (token.includes('technical_marker') || token.includes('technical marker') || token.includes('technical-marker') || token.includes('technicalmarker')) return true
  if (token.includes('technical_exception') || token.includes('technical exception') || token.includes('technical-exception') || token.includes('technicalexception')) return true
  if (token.includes('技术错误') || token.includes('技术异常')) return true
  if (token.includes('write_blocked') || token.includes('write blocked') || token.includes('write-blocked') || token.includes('writeblocked')) return true
  if (token.includes('not_connected') || token.includes('not connected') || token.includes('not-connected') || token.includes('notconnected')) return true
  if (token.includes('need_login') || token === '401' || token.includes('unauthorized')) return true
  if (token.includes('unimplemented') || token.includes('not_implemented') || token.includes('not implemented')) return true
  if (token.includes('not_supported') || token.includes('not supported') || token.includes('unsupported')) return true
  if (token.includes('data_unavailable') || token.includes('data unavailable') || token.includes('data-unavailable') || token.includes('unavailable')) return true
  if (token.includes('query_only') || token.includes('query only') || token.includes('only_query') || token.includes('only query') || token.includes('只支持查询') || token.includes('仅支持查询')) return true
  if (hasFailureWord(value) || ['fail', 'failed', 'failure', 'error', 'rejected', 'reject', 'disabled'].includes(token)) return true
  // Soft words like "fallback/mock/read-only" can be present as metadata notes while real payload is nested.
  return false
}

export function hasUnavailableMarker(payload) {
  if (!payload || typeof payload !== 'object') return false
  const content = contentOf(payload)
  const bodies = responseBodies(payload)
  const hasBusinessPayload = hasPayloadFields(content)
  const falseProofFlags = [
    ...PAYMENT_FALSE_PROOF_KEYS.map((key) => payload[key]),
    ...PAYMENT_FALSE_PROOF_KEYS.map((key) => content[key]),
    ...bodies.flatMap((body) => PAYMENT_FALSE_PROOF_KEYS.map((key) => body[key])),
    ...ORDER_WRITABLE_KEYS.map((key) => payload[key]),
    ...ORDER_WRITABLE_KEYS.map((key) => content[key]),
    ...bodies.flatMap((body) => ORDER_WRITABLE_KEYS.map((key) => body[key]))
  ]
  if (falseProofFlags.some(isFalseProofValue)) return true
  if (hasTechnicalMarkerAlias(payload, content, bodies) || hasBackendFailureAlias(payload, content, bodies)) return true
  const directFlags = [
    payload.readOnly,
    payload.readOnlyAction,
    payload.readOnlyMode,
    payload.readonly,
    payload.read_only,
    payload.read_only_action,
    payload.read_only_mode,
    payload.mock,
    payload.mockMode,
    payload.mock_mode,
    payload.mockFallback,
    payload.mock_fallback,
    payload.unimplemented,
    payload.notImplemented,
    payload.not_implemented,
    payload.notSupported,
    payload.not_supported,
    payload.unsupported,
    payload.dataUnavailable,
    payload.data_unavailable,
    payload.notConnected,
    payload.not_connected,
    payload.fallback,
    payload.fallbackUsed,
    payload.fallback_used,
    payload.failClosed,
    payload.fail_closed,
    payload.placeholder,
    payload.placeholderMode,
    payload.placeholder_mode,
    payload.placeholderResponse,
    payload.placeholder_response,
    payload.queryOnly,
    payload.query_only,
    payload.onlyQuery,
    payload.only_query,
    payload.stub,
    payload.writeBlocked,
    payload.write_blocked,
    payload.blocked,
    payload.backendBlocked,
    payload.backend_blocked,
    payload.paymentBackendBlocked,
    payload.payment_backend_blocked,
    payload.paymentStatusUnconfirmed,
    payload.payment_status_unconfirmed,
    payload.falseProof,
    payload.false_proof,
    payload.readFailure,
    payload.read_failure,
    payload.readUnavailable,
    payload.read_unavailable,
    payload.routeUnavailable,
    payload.route_unavailable,
    payload.technicalError,
    payload.technical_error,
    payload.technicalException,
    payload.technical_exception,
    payload.readBlocked,
    payload.read_blocked,
    payload.readFailed,
    payload.read_failed,
    content.readOnly,
    content.readOnlyAction,
    content.readOnlyMode,
    content.readonly,
    content.read_only,
    content.read_only_action,
    content.read_only_mode,
    content.mock,
    content.mockMode,
    content.mock_mode,
    content.mockFallback,
    content.mock_fallback,
    content.unimplemented,
    content.notImplemented,
    content.not_implemented,
    content.notSupported,
    content.not_supported,
    content.unsupported,
    content.dataUnavailable,
    content.data_unavailable,
    content.notConnected,
    content.not_connected,
    content.fallback,
    content.fallbackUsed,
    content.fallback_used,
    content.failClosed,
    content.fail_closed,
    content.placeholder,
    content.placeholderMode,
    content.placeholder_mode,
    content.placeholderResponse,
    content.placeholder_response,
    content.queryOnly,
    content.query_only,
    content.onlyQuery,
    content.only_query,
    content.stub,
    content.writeBlocked,
    content.write_blocked,
    content.blocked,
    content.backendBlocked,
    content.backend_blocked,
    content.paymentBackendBlocked,
    content.payment_backend_blocked,
    content.paymentStatusUnconfirmed,
    content.payment_status_unconfirmed,
    content.falseProof,
    content.false_proof,
    content.readFailure,
    content.read_failure,
    content.readUnavailable,
    content.read_unavailable,
    content.routeUnavailable,
    content.route_unavailable,
    content.technicalError,
    content.technical_error,
    content.technicalException,
    content.technical_exception,
    content.readBlocked,
    content.read_blocked,
    content.readFailed,
    content.read_failed,
    ...bodies.flatMap((body) => [
      body.readOnly,
      body.readOnlyAction,
      body.readOnlyMode,
      body.readonly,
      body.read_only,
      body.read_only_action,
      body.read_only_mode,
      body.mock,
      body.mockMode,
      body.mock_mode,
      body.mockFallback,
      body.mock_fallback,
      body.unimplemented,
      body.notImplemented,
      body.not_implemented,
      body.notSupported,
      body.not_supported,
      body.unsupported,
      body.dataUnavailable,
      body.data_unavailable,
      body.notConnected,
      body.not_connected,
      body.fallback,
      body.fallbackUsed,
      body.fallback_used,
      body.failClosed,
      body.fail_closed,
      body.placeholder,
      body.placeholderMode,
      body.placeholder_mode,
      body.placeholderResponse,
      body.placeholder_response,
      body.queryOnly,
      body.query_only,
      body.onlyQuery,
      body.only_query,
      body.stub,
      body.writeBlocked,
      body.write_blocked,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.falseProof,
      body.false_proof,
      body.readFailure,
      body.read_failure,
      body.readUnavailable,
      body.read_unavailable,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalException,
      body.technical_exception,
      body.readBlocked,
      body.read_blocked,
      body.readFailed,
      body.read_failed
    ]),
    ...readOnlyQueryGuardValues(payload, content, bodies)
  ]
  if (hasBusinessPayload) {
    if (directFlags.some(isStrictUnavailableMarker)) return true
  } else if (directFlags.some((value) => isActiveGuardFlag(value) || isUnavailableToken(value))) {
    return true
  }
  if ([payload.success, content.success, ...bodies.map((body) => body.success)].some((value) => value === false || unavailableToken(value) === 'false')) return true
  const looseScanValues = [
    payload.status,
    payload.state,
    payload.result,
    payload.marker,
    payload.type,
    payload.code,
    payload.errorCode,
    payload.writeBlocked,
    payload.write_blocked,
    payload.blocked,
    payload.backendBlocked,
    payload.backend_blocked,
    payload.paymentBackendBlocked,
    payload.payment_backend_blocked,
    payload.paymentStatusUnconfirmed,
    payload.payment_status_unconfirmed,
    payload.falseProof,
    payload.false_proof,
    payload.readFailure,
    payload.read_failure,
    payload.readUnavailable,
    payload.read_unavailable,
    payload.notConnected,
    payload.not_connected,
    payload.routeUnavailable,
    payload.route_unavailable,
    payload.technicalError,
    payload.technical_error,
    payload.technicalException,
    payload.technical_exception,
    payload.readBlocked,
    payload.read_blocked,
    payload.readFailed,
    payload.read_failed,
    payload.msg,
    payload.message,
    payload.errorMsg,
    payload.errorMessage,
    payload.reason,
    payload.desc,
    payload.description,
    payload.notSupported,
    payload.not_supported,
    payload.unsupported,
    payload.dataUnavailable,
    payload.data_unavailable,
    payload.fallback,
    payload.failClosed,
    payload.fail_closed,
    payload.placeholder,
    payload.placeholderMode,
    payload.placeholder_mode,
    payload.placeholderResponse,
    payload.placeholder_response,
    payload.queryOnly,
    payload.query_only,
    payload.onlyQuery,
    payload.only_query,
    payload.success,
    content.status,
    content.state,
    content.result,
    content.marker,
    content.type,
    content.code,
    content.errorCode,
    content.writeBlocked,
    content.write_blocked,
    content.blocked,
    content.backendBlocked,
    content.backend_blocked,
    content.paymentBackendBlocked,
    content.payment_backend_blocked,
    content.paymentStatusUnconfirmed,
    content.payment_status_unconfirmed,
    content.readFailure,
    content.read_failure,
    content.readUnavailable,
    content.read_unavailable,
    content.notConnected,
    content.not_connected,
    content.routeUnavailable,
    content.route_unavailable,
    content.technicalError,
    content.technical_error,
    content.technicalException,
    content.technical_exception,
    content.readBlocked,
    content.read_blocked,
    content.readFailed,
    content.read_failed,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    content.notSupported,
    content.not_supported,
    content.unsupported,
    content.dataUnavailable,
    content.data_unavailable,
    content.fallback,
    content.failClosed,
    content.fail_closed,
    content.placeholder,
    content.placeholderMode,
    content.placeholder_mode,
    content.placeholderResponse,
    content.placeholder_response,
    content.queryOnly,
    content.query_only,
    content.onlyQuery,
    content.only_query,
    content.success,
    ...bodies.flatMap((body) => [
      body.status,
      body.state,
      body.result,
      body.marker,
      body.type,
      body.code,
      body.errorCode,
      body.writeBlocked,
      body.write_blocked,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.readFailure,
      body.read_failure,
      body.readUnavailable,
      body.read_unavailable,
      body.notConnected,
      body.not_connected,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalException,
      body.technical_exception,
      body.readBlocked,
      body.read_blocked,
      body.readFailed,
      body.read_failed,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description,
      body.notSupported,
      body.not_supported,
      body.unsupported,
      body.dataUnavailable,
      body.data_unavailable,
      body.fallback,
      body.failClosed,
      body.fail_closed,
      body.placeholder,
      body.placeholderMode,
      body.placeholder_mode,
      body.placeholderResponse,
      body.placeholder_response,
      body.queryOnly,
      body.query_only,
      body.onlyQuery,
      body.only_query,
      body.success
    ])
  ]
  return hasBusinessPayload
    ? looseScanValues.some(isHardUnavailableToken)
    : looseScanValues.some(isUnavailableToken)
}

export function unavailableMessage(res, fallback) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const falseProofFlags = [
    ...PAYMENT_FALSE_PROOF_KEYS.map((key) => res && res[key]),
    ...PAYMENT_FALSE_PROOF_KEYS.map((key) => content[key]),
    ...bodies.flatMap((body) => PAYMENT_FALSE_PROOF_KEYS.map((key) => body[key])),
    ...ORDER_WRITABLE_KEYS.map((key) => res && res[key]),
    ...ORDER_WRITABLE_KEYS.map((key) => content[key]),
    ...bodies.flatMap((body) => ORDER_WRITABLE_KEYS.map((key) => body[key]))
  ]
  const values = [
    res && res.status,
    res && res.state,
    res && res.marker,
    res && res.type,
    res && res.code,
    res && res.errorCode,
    res && res.writeBlocked,
    res && res.write_blocked,
    res && res.blocked,
    res && res.backendBlocked,
    res && res.backend_blocked,
    res && res.paymentBackendBlocked,
    res && res.payment_backend_blocked,
    res && res.paymentStatusUnconfirmed,
    res && res.payment_status_unconfirmed,
    res && res.readFailure,
    res && res.read_failure,
    res && res.readUnavailable,
    res && res.read_unavailable,
    res && res.notConnected,
    res && res.not_connected,
    res && res.routeUnavailable,
    res && res.route_unavailable,
    res && res.technicalError,
    res && res.technical_error,
    res && res.technicalException,
    res && res.technical_exception,
    res && res.readBlocked,
    res && res.read_blocked,
    res && res.readFailed,
    res && res.read_failed,
    res && res.msg,
    res && res.message,
    res && res.errorMsg,
    res && res.errorMessage,
    res && res.reason,
    res && res.desc,
    res && res.description,
    res && res.notSupported,
    res && res.not_supported,
    res && res.unsupported,
    res && res.dataUnavailable,
    res && res.data_unavailable,
    res && res.fallback,
    res && res.failClosed,
    res && res.fail_closed,
    res && res.placeholder,
    res && res.placeholderMode,
    res && res.placeholder_mode,
    res && res.placeholderResponse,
    res && res.placeholder_response,
    res && res.queryOnly,
    res && res.query_only,
    res && res.onlyQuery,
    res && res.only_query,
    res && res.success,
    content.status,
    content.state,
    content.marker,
    content.type,
    content.code,
    content.errorCode,
    content.writeBlocked,
    content.write_blocked,
    content.blocked,
    content.backendBlocked,
    content.backend_blocked,
    content.paymentBackendBlocked,
    content.payment_backend_blocked,
    content.paymentStatusUnconfirmed,
    content.payment_status_unconfirmed,
    content.readFailure,
    content.read_failure,
    content.readUnavailable,
    content.read_unavailable,
    content.notConnected,
    content.not_connected,
    content.routeUnavailable,
    content.route_unavailable,
    content.technicalError,
    content.technical_error,
    content.technicalException,
    content.technical_exception,
    content.readBlocked,
    content.read_blocked,
    content.readFailed,
    content.read_failed,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    content.notSupported,
    content.not_supported,
    content.unsupported,
    content.dataUnavailable,
    content.data_unavailable,
    content.fallback,
    content.failClosed,
    content.fail_closed,
    content.placeholder,
    content.placeholderMode,
    content.placeholder_mode,
    content.placeholderResponse,
    content.placeholder_response,
    content.queryOnly,
    content.query_only,
    content.onlyQuery,
    content.only_query,
    content.success,
    ...bodies.flatMap((body) => [
      body.status,
      body.state,
      body.marker,
      body.type,
      body.code,
      body.errorCode,
      body.writeBlocked,
      body.write_blocked,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.readFailure,
      body.read_failure,
      body.readUnavailable,
      body.read_unavailable,
      body.notConnected,
      body.not_connected,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalException,
      body.technical_exception,
      body.readBlocked,
      body.read_blocked,
      body.readFailed,
      body.read_failed,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description,
      body.notSupported,
      body.not_supported,
      body.unsupported,
      body.dataUnavailable,
      body.data_unavailable,
      body.fallback,
      body.failClosed,
      body.fail_closed,
      body.placeholder,
      body.placeholderMode,
      body.placeholder_mode,
      body.placeholderResponse,
      body.placeholder_response,
      body.queryOnly,
      body.query_only,
      body.onlyQuery,
      body.only_query,
      body.success
    ])
  ]
  if (isNeedLogin(res)) return 'Please log in to continue'
  if (falseProofFlags.some(isFalseProofValue)) return 'This feature is temporarily unavailable. Please try again later.'
  if (hasTechnicalMarkerAlias(res, content, bodies) || hasTechnicalAliasToken(values)) {
    return fallback && fallback !== 'fallback' && !hasRawTechnicalDetail(fallback) && !isOptimisticSuccessMessage(fallback)
      ? fallback
      : 'This feature is temporarily unavailable. Please try again later.'
  }
  if (hasBackendFailureAlias(res, content, bodies) || hasBackendFailureAliasToken(values)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.paymentStatusUnconfirmed, res && res.payment_status_unconfirmed, content.paymentStatusUnconfirmed, content.payment_status_unconfirmed, ...bodies.flatMap((body) => [body.paymentStatusUnconfirmed, body.payment_status_unconfirmed])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => {
    const token = unavailableToken(value)
    return token.includes('payment_status_unconfirmed') || token.includes('paymentstatusunconfirmed') || token.includes('payment status unconfirmed') || token.includes('payment-status-unconfirmed')
  })) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.readFailure, res && res.read_failure, res && res.readUnavailable, res && res.read_unavailable, res && res.readBlocked, res && res.read_blocked, res && res.readFailed, res && res.read_failed, content.readFailure, content.read_failure, content.readUnavailable, content.read_unavailable, content.readBlocked, content.read_blocked, content.readFailed, content.read_failed, ...bodies.flatMap((body) => [body.readFailure, body.read_failure, body.readUnavailable, body.read_unavailable, body.readBlocked, body.read_blocked, body.readFailed, body.read_failed])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value))) {
    return unavailableFallback(fallback, '当前资金数据读取失败，已保留现有数据并阻止假成功展示，请稍后重试。')
  }
  if ([res && res.technicalError, res && res.technical_error, res && res.technicalException, res && res.technical_exception, content.technicalError, content.technical_error, content.technicalException, content.technical_exception, ...bodies.flatMap((body) => [body.technicalError, body.technical_error, body.technicalException, body.technical_exception])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || hasTechnicalAliasToken(values)) {
    return fallback && fallback !== 'fallback' && !hasRawTechnicalDetail(fallback) && !isOptimisticSuccessMessage(fallback)
      ? fallback
      : 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.blocked, res && res.failClosed, res && res.fail_closed, res && res.backendBlocked, res && res.backend_blocked, res && res.paymentBackendBlocked, res && res.payment_backend_blocked, content.blocked, content.failClosed, content.fail_closed, content.backendBlocked, content.backend_blocked, content.paymentBackendBlocked, content.payment_backend_blocked, ...bodies.flatMap((body) => [body.blocked, body.failClosed, body.fail_closed, body.backendBlocked, body.backend_blocked, body.paymentBackendBlocked, body.payment_backend_blocked])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some(isBlockedToken)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.writeBlocked, res && res.write_blocked, content.writeBlocked, content.write_blocked, ...bodies.flatMap((body) => [body.writeBlocked, body.write_blocked])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.readOnly, ...readOnlyQueryGuardValues(res, content, bodies), res && res.readOnlyAction, res && res.readOnlyMode, res && res.readonly, res && res.read_only, res && res.read_only_action, res && res.read_only_mode, content.readOnly, content.readOnlyAction, content.readOnlyMode, content.readonly, content.read_only, content.read_only_action, content.read_only_mode, ...bodies.flatMap((body) => [body.readOnly, body.readOnlyAction, body.readOnlyMode, body.readonly, body.read_only, body.read_only_action, body.read_only_mode])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => isUnavailableToken(value) && unavailableToken(value).includes('read'))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.unimplemented, res && res.notImplemented, res && res.not_implemented, content.unimplemented, content.notImplemented, content.not_implemented, ...bodies.flatMap((body) => [body.unimplemented, body.notImplemented, body.not_implemented])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => isUnavailableToken(value) && (unavailableToken(value).includes('unimplemented') || unavailableToken(value).includes('not_implemented') || unavailableToken(value).includes('not implemented')))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.notSupported, res && res.not_supported, res && res.unsupported, res && res.dataUnavailable, res && res.data_unavailable, res && res.notConnected, res && res.not_connected, res && res.routeUnavailable, res && res.route_unavailable, res && res.fallback, res && res.placeholder, res && res.placeholderMode, res && res.placeholder_mode, res && res.placeholderResponse, res && res.placeholder_response, res && res.queryOnly, res && res.query_only, res && res.onlyQuery, res && res.only_query, content.notSupported, content.not_supported, content.unsupported, content.dataUnavailable, content.data_unavailable, content.notConnected, content.not_connected, content.routeUnavailable, content.route_unavailable, content.fallback, content.placeholder, content.placeholderMode, content.placeholder_mode, content.placeholderResponse, content.placeholder_response, content.queryOnly, content.query_only, content.onlyQuery, content.only_query, ...bodies.flatMap((body) => [body.notSupported, body.not_supported, body.unsupported, body.dataUnavailable, body.data_unavailable, body.notConnected, body.not_connected, body.routeUnavailable, body.route_unavailable, body.fallback, body.placeholder, body.placeholderMode, body.placeholder_mode, body.placeholderResponse, body.placeholder_response, body.queryOnly, body.query_only, body.onlyQuery, body.only_query])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => isUnavailableToken(value) && (unavailableToken(value).includes('unsupported') || unavailableToken(value).includes('fallback') || unavailableToken(value).includes('placeholder') || unavailableToken(value).includes('data unavailable') || unavailableToken(value).includes('data_unavailable') || unavailableToken(value).includes('not_connected') || unavailableToken(value).includes('not connected') || unavailableToken(value).includes('notconnected') || unavailableToken(value).includes('route_unavailable') || unavailableToken(value).includes('route unavailable') || unavailableToken(value).includes('routeunavailable') || unavailableToken(value).includes('query')))) {
    return unavailableFallback(fallback, 'This feature is temporarily unavailable. Please try again later.')
  }
  if ([res && res.stub, content.stub, ...bodies.map((body) => body.stub)].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => unavailableToken(value) === 'stub')) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.mock, res && res.mockMode, res && res.mock_mode, res && res.mockFallback, res && res.mock_fallback, content.mock, content.mockMode, content.mock_mode, content.mockFallback, content.mock_fallback, ...bodies.flatMap((body) => [body.mock, body.mockMode, body.mock_mode, body.mockFallback, body.mock_fallback])].some((value) => isActiveGuardFlag(value) || isUnavailableToken(value)) || values.some((value) => unavailableToken(value).includes('mock'))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if ([res && res.success, content.success, ...bodies.map((body) => body.success)].some((value) => value === false || unavailableToken(value) === 'false')) {
    return fallback || 'This feature is temporarily unavailable. Please try again later.'
  }
  return fallback || 'This feature is temporarily unavailable. Please try again later.'
}

export function isOk(res) {
  if (!res || isNeedLogin(res) || hasUnavailableMarker(res) || hasBusinessFailureMarker(res)) return false
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const okStatuses = [
    res.status,
    res.state,
    content.status,
    content.state,
    ...bodies.flatMap((body) => [body.status, body.state])
  ]
  const okCodes = [
    res.code,
    res.errorCode,
    content.code,
    content.errorCode,
    ...bodies.flatMap((body) => [body.code, body.errorCode])
  ]
  const okSuccess = [
    res.success,
    content.success,
    ...bodies.map((body) => body.success)
  ]
  return okStatuses.some(isOkStatusToken) ||
    okCodes.some(isOkCodeToken) ||
    okSuccess.some((value) => value === true || unavailableToken(value) === 'true')
}

export function isSafeWriteSuccess(res) {
  return isOk(res) && hasWriteSuccessProof(res)
}

export function hasStrictWriteProof(res) {
  return hasWriteSuccessProof(res)
}

export function contentOf(res) {
  if (!res || typeof res !== 'object') return {}
  return unwrapEnvelopeBody(firstEnvelopeBody(res))
}

function responseBodies(res) {
  if (!res || typeof res !== 'object') return []
  return collectEnvelopeBodies(res)
}

function firstEnvelopeBody(source) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object') return null
  const candidates = []
  let firstEmptyEnvelope = null
  for (const key of ENVELOPE_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) {
      candidates.push(value)
      continue
    }
    if (value && typeof value === 'object') {
      if (Object.keys(value).length) candidates.push(value)
      if (!firstEmptyEnvelope) firstEmptyEnvelope = value
    }
  }
  const populated = candidates.find((value) => hasPayloadFields(unwrapEnvelopeBody(value)))
  if (populated) return populated
  const nonEmptyEnvelope = candidates.find((value) => Object.keys(normalizeEnvelopeBody(value)).length)
  if (nonEmptyEnvelope) return nonEmptyEnvelope
  if (firstEmptyEnvelope) return firstEmptyEnvelope
  return source
}

function normalizeEnvelopeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return value && typeof value === 'object' ? value : {}
}

function unwrapEnvelopeBody(value, depth = 0) {
  const body = normalizeEnvelopeBody(value)
  if (!Object.keys(body).length || depth >= MAX_ENVELOPE_DEPTH) return body
  const nestedValues = ENVELOPE_KEYS
    .map((key) => body[key])
    .filter((nested) => nested && (typeof nested === 'object' || Array.isArray(nested)))
  const populated = nestedValues.find((nested) => hasPayloadFields(unwrapEnvelopeBody(nested, depth + 1)))
  const orderedNestedValues = populated
    ? [populated, ...nestedValues.filter((nested) => nested !== populated)]
    : nestedValues
  for (const nested of orderedNestedValues) {
    const unwrapped = unwrapEnvelopeBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    ENVELOPE_KEYS.forEach((envelopeKey) => {
      delete parent[envelopeKey]
    })
    return {
      ...parent,
      ...unwrapped
    }
  }
  return body
}

function hasPayloadFields(source) {
  if (!source || typeof source !== 'object') return false
  return Object.keys(source).some((key) => {
    if (PAYLOAD_HINT_KEYS.includes(key)) return true
    if (RESPONSE_METADATA_KEYS.includes(key)) return false
    const value = source[key]
    if (Array.isArray(value)) return value.length > 0
    if (value && typeof value === 'object') return Object.keys(value).length > 0
    return value !== undefined && value !== null && String(value).trim() !== ''
  })
}

function collectEnvelopeBodies(source, depth = 0, out = []) {
  if (!source || typeof source !== 'object' || depth >= MAX_ENVELOPE_DEPTH) return out
  if (Array.isArray(source)) {
    source.forEach((item) => {
      if (item && typeof item === 'object') {
        out.push(item)
        collectEnvelopeBodies(item, depth + 1, out)
      }
    })
    return out
  }
  for (const key of ENVELOPE_KEYS) {
    const value = source[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) continue
    const body = normalizeEnvelopeBody(value)
    if (!Object.keys(body).length) continue
    out.push(body)
    collectEnvelopeBodies(value, depth + 1, out)
  }
  return out
}

function hasWriteSuccessProof(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return [res, content, ...bodies].some((source) => {
    if (!source || typeof source !== 'object') return false
    return WRITE_PROOF_KEYS.some((key) => hasMeaningfulValue(source[key])) ||
      SERVER_WRITE_PROOF_KEYS.some((key) => hasMeaningfulServerWriteProof(source[key])) ||
      WRITE_COUNT_PROOF_KEYS.some((key) => hasPositiveCount(source[key]))
  })
}

function hasMeaningfulServerWriteProof(value) {
  if (value === true || unavailableToken(value) === 'true') return false
  return hasMeaningfulValue(value)
}

function hasMeaningfulValue(value) {
  // For write success, require a primitive server identifier (id/token/count),
  // not arrays/objects that can be placeholder structures.
  if (Array.isArray(value)) return false
  if (value && typeof value === 'object') return false
  if (typeof value === 'boolean') return value === true
  const valueText = text(value).trim()
  if (!valueText) return false
  const token = unavailableToken(valueText)
  if (FALSEY_PROOF_TOKENS.includes(token)) return false
  if (isUnavailableToken(token)) return false
  return true
}

function hasPositiveCount(value) {
  const token = unavailableToken(value)
  if (!/^\d+$/.test(token)) return false
  return Number(token) > 0
}

export function failMessage(res, fallback) {
  if (isNeedLogin(res)) return 'Please log in to continue'
  if (hasUnavailableMarker(res)) {
    const message = firstMessage(res)
    if (message && !isOptimisticSuccessMessage(message) && (isVisibleTechnicalFailureMessage(message) || !isHardUnavailableText(message))) return message
    return unavailableMessage(res, fallback)
  }
  return firstMessage(res) || fallback || 'Error'
}

export function handleNeedLogin(vm, res, message = 'Please log in to continue') {
  if (!isNeedLogin(res)) return false
  promptLogin(vm, failMessage(res, message))
  return true
}

function clearLocalToken() {
  return safeRemoveStorageItem(getLocalStorage(), 'token')
}

function browserGlobal() {
  try {
    if (typeof window !== 'undefined') return window
  } catch (e) {
    return null
  }
  return null
}

function getLocalStorage() {
  try {
    const root = browserGlobal()
    const storage = root && root.localStorage
    return storage && typeof storage.removeItem === 'function' ? storage : null
  } catch (e) {
    return null
  }
}

function safeRemoveStorageItem(storage, key) {
  try {
    if (!storage || typeof storage.removeItem !== 'function') return false
    storage.removeItem(key)
    return true
  } catch (e) {
    return false
  }
}

function promptLogin(vm, message) {
  clearLocalToken()
  if (vm && typeof vm.$toast === 'function') {
    vm.$toast({ message: message || 'Please log in to continue', icon: 'info' })
  }
  if (vm && vm.$bus && typeof vm.$bus.$emit === 'function') {
    vm.$bus.$emit('openLogin')
  }
  if (vm && typeof vm.$jumpTo === 'function') {
    vm.$jumpTo('/home', {}, { replace: true })
  }
}

function hasBusinessFailureMarker(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return [res, content, ...bodies].some((source) => {
    if (!source || typeof source !== 'object') return false
    return [
      source.status,
      source.state,
      source.result
    ].some(isFailureStatusToken) || [
      source.code,
      source.errorCode
    ].some(isFailureCodeToken)
  })
}

function isFailureStatusToken(value) {
  const token = unavailableToken(value)
  return ['fail', 'failed', 'failure', 'error', 'rejected', 'reject', 'disabled'].includes(token)
}

function isOkStatusToken(value) {
  const token = unavailableToken(value)
  return ['ok', 'success', 'successful', 'succeed', 'succeeded'].includes(token)
}

function isFailureCodeToken(value) {
  const token = unavailableToken(value)
  if (!token || isOkCodeToken(token) || token === 'need_login') return false
  if (/^[45]\d{2,}$/.test(token)) return true
  return hasFailureWord(token) || token.startsWith('e_')
}

function isOkCodeToken(value) {
  const token = unavailableToken(value)
  return ['0', '00', '0000', '000000', '200', 'ok', 'success'].includes(token)
}

function firstMessage(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return firstValueFromSources([res, content, ...bodies], [
    'msg',
    'message',
    'errorMsg',
    'errorMessage',
    'reason',
    'desc',
    'description',
    'orderStatusName'
  ])
}

function isOptimisticSuccessMessage(value) {
  const token = unavailableToken(value)
  if (!token) return false
  const hasSuccessCopy = /\b(?:ok|success|successful|done|saved|complete|completed|sucesso)\b/.test(token) || [
    'ok',
    'success',
    'successful',
    'done',
    'saved',
    'complete',
    'completed',
    'sucesso'
  ].includes(token) || token.includes('成功')
  const hasFailureContext = hasFailureWord(token) ||
    isUnavailableToken(token) ||
    isBlockedToken(token) ||
    token.includes('阻止') ||
    token.includes('失败') ||
    token.includes('失敗') ||
    token.includes('不可用') ||
    token.includes('未接入') ||
    token.includes('fake') ||
    token.includes('只读') ||
    token.includes('只讀')
  return hasSuccessCopy && !hasFailureContext
}

function firstValueFromSources(sources, keys) {
  for (const source of sources) {
    const value = firstValue(source, keys)
    if (text(value)) return value
  }
  return ''
}

export function toNumber(value, fallback = 0) {
  const normalized = normalizeNumericValue(value)
  const num = Number(normalized)
  return Number.isFinite(num) ? num : fallback
}

function normalizeNumericValue(value) {
  if (typeof value === 'number') return value
  const raw = text(value)
  if (!raw) return raw
  const cleaned = raw.replace(/[^\d.,-]/g, '')
  if (!cleaned) return cleaned
  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')
  if (lastComma > -1 && lastDot > -1) {
    return lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '')
  }
  if (lastComma > -1) {
    const decimals = cleaned.length - lastComma - 1
    return decimals === 3 ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.')
  }
  if ((cleaned.match(/\./g) || []).length > 1) {
    const lastSeparator = cleaned.lastIndexOf('.')
    return cleaned.slice(0, lastSeparator).replace(/\./g, '') + cleaned.slice(lastSeparator)
  }
  return cleaned
}

function text(value) {
  return String(value == null ? '' : value).trim()
}

function lower(value) {
  return text(value).toLowerCase()
}

function firstValue(source, keys) {
  const data = source && typeof source === 'object' ? source : {}
  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null && text(data[key])) {
      return data[key]
    }
  }
  return ''
}

export function normalizeOrderType(item = {}) {
  const rawType = lower(firstValue(item, ['orderType', 'type', 'dealType', 'recordType', 'transactionType', 'businessType']))
  const rawStatus = lower(firstValue(item, ['orderStatus', 'status', 'state', 'statusCode']))
  if (rawType === 'wd' || rawType === 'w' || rawType.includes('withdraw') || rawType.includes('saque') || rawStatus.includes('withdraw')) return 'wd'
  if (rawType === 'pay' || rawType === 'p' || rawType.includes('recharge') || rawType.includes('deposit') || rawStatus.includes('pay')) return 'pay'
  return rawType || rawStatus ? 'pay' : ''
}

export function normalizeOrderStatus(item = {}, orderType = normalizeOrderType(item)) {
  const prefix = orderType === 'wd' ? 'withdraw' : 'pay'
  if (hasUnavailableMarker(item)) return `${prefix}$failed`
  const raw = lower(firstValue(item, ['orderStatus', 'status', 'state', 'statusCode']))
  if (!raw) return ''
  if (raw === 'withdraw$success' || raw === 'pay$success') return hasPositivePaymentStatusProof(item) ? raw : `${prefix}$pending`
  if (raw === 'withdraw$failed' || raw === 'pay$failed') return raw
  if (['1', 'success', 'succeed', 'succeeded', 'complete', 'completed', 'paid', 'approved', 'done', 'finish', 'finished'].includes(raw) || raw.includes('success') || raw.includes('paid') || raw.includes('complete')) {
    if (!hasPositivePaymentStatusProof(item)) return `${prefix}$pending`
    return `${prefix}$success`
  }
  if (isTerminalFailureToken(raw)) {
    return `${prefix}$failed`
  }
  return `${prefix}$pending`
}

export function isSuccessfulOrderStatus(status) {
  return ['withdraw$success', 'pay$success'].includes(status)
}

export function isFailedOrderStatus(status) {
  return ['withdraw$failed', 'pay$failed'].includes(status)
}

export function isFinalOrderStatus(status) {
  return isSuccessfulOrderStatus(status) || isFailedOrderStatus(status)
}

export function displayOrderStatusName(item = {}, lang = {}) {
  if (isSuccessfulOrderStatus(item.orderStatus)) {
    if (item.orderType === 'pay') return lang.recharge_success || 'Recharge Successful'
    return item.orderStatusName || lang.Sucesso || 'Success'
  }
  if (item.orderStatusName) return item.orderStatusName
  if (isFailedOrderStatus(item.orderStatus)) return lang.common_txt376 || 'Failed'
  if (item.orderStatus && item.orderStatus.indexOf('$pending') > -1) return lang.common_txt67 || 'Pending'
  return '-'
}

export function normalizeOrderRecord(item = {}, lang = {}) {
  const orderType = normalizeOrderType(item)
  const orderStatus = normalizeOrderStatus(item, orderType)
  const orderNo = firstValue(item, ['orderNo', 'recordNo', 'record_no', 'wdOrderNo', 'wd_order_no', 'tradeNo', 'payOrderNo', 'paymentOrderNo', 'applyNo', 'id'])
  const orderAmount = item.orderAmount != null ? item.orderAmount : item.amount
  const rawOrderStatusName = hasUnavailableMarker(item) ? '' : text(firstValue(item, ['orderStatusName', 'statusName', 'status_name', 'stateName']))
  const orderStatusName = !isSuccessfulOrderStatus(orderStatus) && isOptimisticSuccessMessage(rawOrderStatusName) ? '' : rawOrderStatusName
  return {
    orderNo: text(orderNo),
    orderType,
    orderTypeName: text(firstValue(item, ['orderTypeName', 'typeName', 'dealTypeName'])) || (orderType ? (orderType === 'wd' ? (lang.Saque || 'Withdraw') : (lang.Recarregar || 'Recharge')) : ''),
    orderAmount: toNumber(orderAmount),
    orderStatus,
    orderStatusName,
    currency: text(firstValue(item, ['currency'])),
    createDate: text(firstValue(item, ['createDate', 'create_date', 'createdAt', 'created_at', 'createTime', 'createdTime', 'updatedAt', 'time'])),
    bankName: text(firstValue(item, ['bankName', 'bank_name'])),
    bankCard: text(firstValue(item, ['bankCard', 'bank_card', 'cardNo', 'card_no', 'accountNo', 'account_no', 'cardNumber', 'card_number'])),
    remark: text(firstValue(item, ['remark', 'memo', 'reason'])),
    isReversed: item.isReversed === true || item.isReversed === 'true'
  }
}

export function hasPositivePaymentStatusProof(item = {}) {
  const content = contentOf(item)
  const bodies = responseBodies(item)
  return [item, content, ...bodies].some((source) => {
    if (!source || typeof source !== 'object') return false
    return PAYMENT_STATUS_PROOF_KEYS.some((key) => isPositiveProofValue(source[key]))
  })
}

export function firstArray(content, keys) {
  const source = content && typeof content === 'object' ? content : {}
  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key]
  }
  for (const wrapper of ENVELOPE_KEYS) {
    if (source[wrapper] && typeof source[wrapper] === 'object') {
      const nested = firstArray(source[wrapper], keys)
      if (nested.length) return nested
    }
  }
  return []
}

export function safeParseJson(value, fallback = {}) {
  if (!value || typeof value !== 'string') return fallback
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed : fallback
  } catch (e) {
    return fallback
  }
}
